import { useState } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Field,
  Select,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCreateReservation } from '../../hooks/useReservations';
import { useSlots } from '../../hooks/useSlots';
import { useDateLocale } from '../../hooks/useDateLocale';
import { BUSINESS_HOURS, type SlotId } from '@cp-parkingslots/shared';
import { ApiError } from '../../services/api';

const useStyles = makeStyles({
  surface: {
    maxWidth: '450px',
    width: '100%',
    '@media (max-width: 599px)': {
      maxWidth: '100%',
      margin: tokens.spacingHorizontalM,
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  timeRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
  },
  timeField: {
    flex: 1,
  },
  dateDisplay: {
    padding: tokens.spacingVerticalS,
    '@media (max-width: 599px)': {
      fontSize: tokens.fontSizeBase300,
    },
  },
  actions: {
    '@media (max-width: 599px)': {
      flexDirection: 'column-reverse',
      gap: tokens.spacingVerticalS,
      '& button': {
        width: '100%',
      },
    },
  },
});

interface ReservationDialogProps {
  open: boolean;
  onClose: () => void;
  date: Date;
  hour: number;
  slotId: string;
}

export default function ReservationDialog({
  open,
  onClose,
  date,
  hour,
  slotId: initialSlotId,
}: ReservationDialogProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { data: slots } = useSlots();
  const createReservation = useCreateReservation();

  const [selectedSlotId, setSelectedSlotId] = useState(initialSlotId);
  const [startHour, setStartHour] = useState(hour);
  const [endHour, setEndHour] = useState(Math.min(hour + 1, BUSINESS_HOURS.endHour));
  const [error, setError] = useState<string | null>(null);

  const hours = [];
  for (let i = BUSINESS_HOURS.startHour; i <= BUSINESS_HOURS.endHour; i++) {
    hours.push(i);
  }

  const handleSubmit = async () => {
    setError(null);

    if (endHour <= startHour) {
      setError(t('reservation.errorEndTime'));
      return;
    }

    try {
      await createReservation.mutateAsync({
        slotId: selectedSlotId as SlotId,
        date: format(date, 'yyyy-MM-dd'),
        startHour,
        endHour,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t('reservation.errorUnexpected'));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{t('reservation.title')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field label={t('reservation.date')}>
                <div className={styles.dateDisplay}>
                  {format(date, 'EEEE, d. MMMM yyyy', { locale: dateLocale })}
                </div>
              </Field>

              <Field label={t('reservation.slot')}>
                <Select
                  value={selectedSlotId}
                  onChange={(_, data) => setSelectedSlotId(data.value)}
                >
                  {slots?.map((slot) => (
                    <option key={slot.id} value={slot.id}>
                      {t(`slots.${slot.id}` as const)}
                    </option>
                  ))}
                </Select>
              </Field>

              <div className={styles.timeRow}>
                <Field label={t('reservation.from')} className={styles.timeField}>
                  <Select
                    value={startHour.toString()}
                    onChange={(_, data) => setStartHour(parseInt(data.value, 10))}
                  >
                    {hours.slice(0, -1).map((h) => (
                      <option key={h} value={h}>
                        {h.toString().padStart(2, '0')}:00
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label={t('reservation.until')} className={styles.timeField}>
                  <Select
                    value={endHour.toString()}
                    onChange={(_, data) => setEndHour(parseInt(data.value, 10))}
                  >
                    {hours.slice(1).map((h) => (
                      <option key={h} value={h}>
                        {h.toString().padStart(2, '0')}:00
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              {error && (
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions className={styles.actions}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">{t('reservation.cancel')}</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={createReservation.isPending}
            >
              {createReservation.isPending ? (
                <Spinner size="tiny" />
              ) : (
                t('reservation.reserve')
              )}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
